<template>
<q-page class="main">
  <common-form :action="$route('project.update', {project: project.id})" is-update>
    <input type="hidden" name="id" :value="project.id" />
    <input type="hidden" name="isNew" :value="$isNew(project) ? 1 : 0" />
    <q-card class="page-card">
      <BaseMap class="page-card__map"
               :geometry="project.json_polygon"
               auto-fit
               canFullscreen
               canZoom
               :canPan="false" />
      <q-card-section class="page-card__main-content">
        <div :class="['row', $isDesktop ? 'q-col-gutter-lg' : 'q-col-gutter-md']">
          <div class="col-12 col-md-6">
            <div :class="['row', $isDesktop ? 'q-col-gutter-lg' : 'q-col-gutter-md']">
              <div class="col-12">
                <common-input
                    v-model="form.title"
                    :label="fieldLabels.title"
                    name="title"
                    :error-message="$errorMessage('title')"
                    :error="$hasError('title')"
                    required
                    autofocus
                />
              </div>
              <div class="col-12">
                <common-input
                    v-model="form.reference_number"
                    :label="fieldLabels.reference_number"
                    name="reference_number"
                    :error-message="$errorMessage('reference_number')"
                    :error="$hasError('reference_number')"
                />
              </div>
              <div v-if="$isNew(project)" class="col-12">
                <div class="text-weight-medium">Selecteer de huidige fase van dit project:</div>
                <option-group
                    v-model="form.is_active"
                    :options="phaseOptions"
                    :label="fieldLabels.is_active"
                    name="is_active"
                    required
                    :error-message="$errorMessage('is_active')"
                    :error="$hasError('is_active')"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div :class="['row', $isDesktop ? 'q-col-gutter-lg' : 'q-col-gutter-md']">
              <div class="col-12">
                <common-input
                    v-model="form.manager"
                    :label="fieldLabels.manager"
                    placeholder="naam en of emailadres"
                    name="manager"
                    :error-message="$errorMessage('manager')"
                    :error="$hasError('manager')"
                    required
                />
              </div>
              <div class="col-12">
                <common-input
                    v-model="form.planner"
                    :label="fieldLabels.planner"
                    placeholder="naam en of emailadres"
                    name="planner"
                    :error-message="$errorMessage('planner')"
                    :error="$hasError('planner')"
                />
              </div>
              <div class="col-12">
                <common-input
                    v-model="form.designer"
                    :label="fieldLabels.designer"
                    placeholder="naam en of emailadres"
                    name="designer"
                    :error-message="$errorMessage('designer')"
                    :error="$hasError('designer')"
                />
              </div>
              <div class="col-12">
                <common-input
                    v-model="form.client"
                    :label="fieldLabels.client"
                    name="client"
                    :error-message="$errorMessage('client')"
                    :error="$hasError('client')"
                    required
                />
              </div>
              <div class="col-12">
                <common-input
                    v-model="form.completion_date"
                    :label="fieldLabels.completion_date"
                    name="completion_date"
                    :error-message="$errorMessage('completion_date')"
                    :error="$hasError('completion_date')"
                />
              </div>

              <div class="col-12" v-if="themes.length">
                <q-select v-model="form.themes"
                          name="themes[]"
                          :options="themes"
                          :label="fieldLabels.themes"
                          option-value="id"
                          outlined
                          multiple
                          use-chips
                          map-options
                          emit-value
                          :error-message="$errorMessage('themes')"
                          :error="$hasError('themes')">
                  <template v-slot:selected-item="scope">
                    <q-chip
                        dense
                        removable
                        @remove="scope.removeAtIndex(scope.index)"
                        :tabindex="scope.tabindex"
                        class="common-chip"
                    >
                      {{ scope.opt.title }}
                    </q-chip>
                  </template>
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section>
                        <q-item-label>{{ scope.opt.title }}</q-item-label>
                        <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>
            </div>
          </div>
          <div class="col-12">
            <common-editor
                v-model="form.description"
                :label="fieldLabels.description"
                name="description"
                type="textarea"
                required
                :error-message="$errorMessage('description')"
                :error="$hasError('description')" />
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right" class="q-pa-md">
        <accent-button label="Opslaan" type="submit" />
      </q-card-actions>
    </q-card>
    <sticky-top-bar />
  </common-form>
</q-page>
</template>

<script>
import { projectMixin } from '~HIOR/mixins'
import { config } from '~INSTANCE'

export default {
  components: {},
  mixins: [projectMixin],
  props: {
    project: {
      type: Object,
      required: false,
    },
    themes: {
      type: Array,
      required: false,
    },
  },
  data () {
    return {
      form: {
        title: this.$old('title', this.project?.latest_version?.title),
        reference_number: this.$old('reference_number', this.project?.reference_number),
        is_active: this.$old('is_active', this.project?.latest_version?.is_active),
        description: this.$old('description', this.project?.latest_version?.description),
        manager: this.$old('manager', this.project?.latest_version?.manager),
        planner: this.$old('planner', this.project?.latest_version?.planner),
        designer: this.$old('designer', this.project?.latest_version?.designer),
        client: this.$old('client', this.project?.latest_version?.client),
        completion_date: this.$old('completion_date', this.project?.latest_version?.completion_date),
        themes: this.$old('themes', this.project?.themes || [])
            .map(theme => parseInt(theme?.id || theme)),
      },
    }
  },
  computed: {
    phaseOptions () {
      return this.project.phases.map((phase) => {
        return {
          value: phase.id,
          label: phase.title,
        }
      })
    },
    themeOptions () {
      return this.themes.map((theme) => {
        return {
          value: theme.id,
          label: theme.title,
          description: theme.description,
        }
      })
    },
    fieldLabels () {
      return {
        title: config.translations?.project?.title ?? 'Titel',
        reference_number: config.translations?.project?.reference_number ?? 'Projectnummer',
        is_active: config.translations?.project?.is_active ?? 'Selecteer de huidige fase van dit project:',
        manager: config.translations?.project?.manager ?? 'Projectleider',
        planner: config.translations?.project?.planner ?? 'Werkvoorbereider',
        designer: config.translations?.project?.designer ?? 'Ontwerper',
        client: config.translations?.project?.client ?? 'Opdrachtgever(s)',
        completion_date: config.translations?.project?.completion_date ?? 'Opleverdatum',
        themes: config.translations?.project?.themes ?? 'Thema\'s',
        description: config.translations?.project?.description ?? 'Aanleiding, doel en verdere toelichting',
      }
    },
  },
}
</script>
